import React from "react"
import { Box, Divider, Stack, Typography, useTheme } from "@carrotfertility/carotene-core"
import { Form, FormAlert, FormButton, useFormContext, useWatch } from "@carrotfertility/carotene-core-x"
import { CustomModule, FormField, SUPPORTED_TYPES, fieldName, isInput, toDefaultValue } from "./FormField"
import { computeVisibleIds } from "./conditions"
import { HealthieAnswer, serializeAnswers } from "./answers"

// The form's values are dynamic: one entry per rendered question, keyed by `fieldName(mod)`
// (`q_<id>`). The questions come from Healthie at runtime, so the shape is a string-keyed record
// rather than named fields — this is the intake form's answer here to the repo's typed-Form convention.
interface IntakeFormValues {
  [questionField: string]: unknown
}

interface IntakeFormProps {
  form: { name: string | null; custom_modules: CustomModule[] }
  onSubmit?: (answers: HealthieAnswer[]) => void | Promise<void>
  submitError?: boolean
  // Called once when the form contains question types this renderer drops, so the host can
  // report it (e.g. to Sentry). Best-effort: omit it and unsupported types are dropped silently.
  onError?: (message: string) => void
  submitLabel?: string
}

const byPosition = (a: CustomModule, b: CustomModule): number => (a.position ?? 0) - (b.position ?? 0)

function buildDefaultValues(modules: CustomModule[]): IntakeFormValues {
  const defaults: IntakeFormValues = {}
  for (const mod of modules) {
    if (isInput(mod)) defaults[fieldName(mod)] = toDefaultValue(mod, "")
  }
  return defaults
}

function IntakeFormBody({ modules }: { modules: CustomModule[] }): JSX.Element {
  const values = useWatch<IntakeFormValues>()
  const visibleIds = computeVisibleIds(modules, values)
  const visible = modules.filter((mod) => visibleIds.has(mod.id) && SUPPORTED_TYPES.has(mod.mod_type))

  return (
    <Box sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.xl) })}>
      {visible.map((mod, i) => (
        <React.Fragment key={mod.id}>
          {i > 0 && <Divider />}
          <FormField mod={mod} />
        </React.Fragment>
      ))}
    </Box>
  )
}

function SubmitButton({ label }: { label: string }): JSX.Element {
  const {
    formState: { isSubmitting }
  } = useFormContext()
  return (
    <FormButton
      type="submit"
      sx={(theme) => ({ alignSelf: "flex-start", marginBlockStart: theme.spacing(theme.tokens.spacing.lg) })}
    >
      {isSubmitting ? "Submitting…" : label}
    </FormButton>
  )
}

export function IntakeForm({
  form,
  onSubmit,
  submitError,
  onError,
  submitLabel = "Submit"
}: IntakeFormProps): JSX.Element {
  const theme = useTheme()
  const modules = [...form.custom_modules].sort(byPosition)

  // Flag question types we can't render once per form (not per re-render), so a form the clinical
  // team builds with a type we drop doesn't fail silently. Keyed on the type list so it fires once.
  const unsupportedTypes = [...new Set(modules.filter((m) => !SUPPORTED_TYPES.has(m.mod_type)).map((m) => m.mod_type))]
  const unsupportedKey = unsupportedTypes.join(",")
  React.useEffect(() => {
    if (unsupportedKey) {
      onError?.(`Telemedicine intake form "${form.name ?? ""}" has unrendered question type(s): ${unsupportedKey}`)
    }
  }, [unsupportedKey, form.name, onError])

  return (
    <Form<IntakeFormValues>
      defaultValues={buildDefaultValues(modules)}
      onSubmit={(values) => onSubmit?.(serializeAnswers(modules, values, computeVisibleIds(modules, values)))}
    >
      <Stack spacing={theme.tokens.spacing.lg}>
        {submitError ? (
          <FormAlert severity="error">Something went wrong submitting your answers. Please try again.</FormAlert>
        ) : null}
        {form.name ? <Typography variant="h3">{form.name}</Typography> : null}
        <IntakeFormBody modules={modules} />
        {onSubmit ? <SubmitButton label={submitLabel} /> : null}
      </Stack>
    </Form>
  )
}
